<template>
  <vc-date-picker
    ref="datePickerRef"
    class="ui-date-picker"
    :model-value="pickerValue"
    :is-range="mode === 'range'"
    :columns="columns"
    :disable-page-swipe="true"
    :locale="locale"
    :popover="false"
    color="bemob-default"
    :min-date="minDate"
    :max-date="maxDate"
    @update:model-value="onInput"
    @drag="onDrag"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from '@/composables';
import { $emitter } from '@/services/emitter';

export interface UiDatePickerDate {
  start: Date;
  end: Date;
  timezone: string;
}

const props = withDefaults(
  defineProps<{
    modelValue?: UiDatePickerDate | string;
    value?: UiDatePickerDate | string;
    columns?: number;
    mode?: string;
    minDate?: string | Date | null;
    maxDate?: string | Date | null;
  }>(),
  {
    mode: 'range',
    minDate: null,
    maxDate: null,
  },
);

const emit = defineEmits<{
  input: [value: UiDatePickerDate | string];
  'update:modelValue': [value: UiDatePickerDate | string];
  drag: [value: UiDatePickerDate | string | null];
}>();

const { locale } = useI18n();

const datePickerRef = ref<{
  adjustPageRange?: () => void;
  moveToValue?: (target: 'start' | 'end') => void | Promise<boolean>;
  dragValue?: unknown;
} | null>(null);

const pickerValue = computed(() =>
  props.modelValue !== undefined ? props.modelValue : props.value,
);

const onInput = (event: UiDatePickerDate | string) => {
  emit('input', event);
  emit('update:modelValue', event);
};

const onDrag = (event: UiDatePickerDate | string | null) => {
  emit('drag', event);
};

const adjustPageRange = () => {
  const picker = datePickerRef.value;
  if (picker?.adjustPageRange) {
    picker.adjustPageRange();
    return;
  }
  picker?.moveToValue?.('start');
};

const resetDateRange = () => {
  if (datePickerRef.value?.dragValue) {
    datePickerRef.value.dragValue = null;
  }
};

onMounted(() => {
  $emitter.on('resetDateRange', resetDateRange);
});

onUnmounted(() => {
  $emitter.off('resetDateRange', resetDateRange);
});

defineExpose({
  adjustPageRange,
});
</script>

<style lang="scss">
.ui-date-picker {
  --vc-accent-200: var(--color-gray10);
  --vc-accent-600: var(--color-green);
  --vc-accent-700: var(--color-green);
  --vc-accent-900: var(--color-graphite);
  --vc-header-arrow-color: var(--color-green);
  --vc-highlight-outline-bg: var(--color-white);
  --vc-highlight-outline-border: var(--color-green);
  --vc-highlight-outline-content-color: inherit;
  --vc-highlight-light-bg: var(--color-gray10);
  --vc-highlight-light-content-color: inherit;
  --vc-highlight-solid-bg: var(--color-green);
  --vc-highlight-solid-content-color: var(--color-white);

  .vc-highlight-content-outline,
  .vc-highlight-content-light,
  .vc-text-bemob-default-900 {
    font-weight: normal;
  }

  .vc-bg-bemob-default-200 {
    background-color: var(--color-gray10);
    font-style: normal;
  }

  .vc-bg-white {
    border-color: var(--color-green);
  }

  .vc-bg-bemob-default-600 {
    background-color: var(--color-green);
  }

  .vc-arrows-container {
    .vc-text-gray-600 {
      color: var(--color-green);
    }

    .hover\:vc-opacity-50:hover {
      opacity: 1;
    }
  }
}
</style>
